"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import { Loader2, RotateCcw, Upload } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/app/_module/components/ui/card";
import { Button } from "@/app/_module/components/ui/button";
import { Input } from "@/app/_module/components/ui/input";
import { Label } from "@/app/_module/components/ui/label";
import { Slider } from "@/app/_module/components/ui/slider";
import { Switch } from "@/app/_module/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/app/_module/components/ui/tabs";
import {
  Dialog,
  ResponsiveDialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/app/_module/components/ui/dialog";
import { useToast } from "@/app/_module/hooks/use-toast";
import { brandFontCssUrls, brandFontStack, ensureFontCss, isBrandFont, type BrandFont } from "@/app/_module/lib/brandFonts";
import { useBranding } from "@/app/_module/store/contexts/brandingContext";
import { useUserContext } from "@/app/_module/store/contexts/userContext/userContexts";
import axiosInstance from "@/app/_module/services/axiosConfig";
import type { AgencyBranding } from "@/app/_module/types/branding";
import { DEFAULT_BRANDING } from "@/app/_module/types/branding";
import { FontPicker } from "./FontPicker";
import { BrandingLockedCard, OwnerBrandingProgramCard, unlockNote, useBrandingAccess } from "./OwnerBranding";

interface BrandingFormData {
  brandName: string;
  brandTagline: string;
  supportEmail: string;
  supportPhone: string;
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  sidebarColor: string;
  sidebarTextColor: string;
  sidebarAccentColor: string;
  sidebarAccentTextColor: string;
  sidebarHeaderTextColor: string;
  textFont: BrandFont | null;
  headingFont: BrandFont | null;
  logoUrl: string;
  logoSize: number;
  faviconUrl: string;
  loginBackgroundUrl: string;
  hidePoweredBy: boolean;
  brandingScope: 'self_only' | 'cascade';
}

const defaultFormData: BrandingFormData = {
  brandName: "",
  brandTagline: "",
  supportEmail: "",
  supportPhone: "",
  primaryColor: "",
  secondaryColor: "",
  accentColor: "",
  sidebarColor: "",
  sidebarTextColor: "",
  sidebarAccentColor: "",
  sidebarAccentTextColor: "",
  sidebarHeaderTextColor: "",
  textFont: null,
  headingFont: null,
  logoUrl: "",
  logoSize: 32,
  faviconUrl: "",
  loginBackgroundUrl: "",
  hidePoweredBy: false,
  brandingScope: 'self_only',
};

type ColorKey =
  | "primaryColor"
  | "secondaryColor"
  | "accentColor"
  | "sidebarColor"
  | "sidebarTextColor"
  | "sidebarAccentColor"
  | "sidebarAccentTextColor"
  | "sidebarHeaderTextColor";

const COLOR_GROUPS: { title: string; fields: { key: ColorKey; label: string }[] }[] = [
  {
    title: "Buttons and highlights",
    fields: [
      { key: "primaryColor", label: "Primary" },
      { key: "secondaryColor", label: "Secondary" },
      { key: "accentColor", label: "Accent" },
    ],
  },
  {
    title: "Sidebar",
    fields: [
      { key: "sidebarColor", label: "Background" },
      { key: "sidebarTextColor", label: "Text" },
      { key: "sidebarAccentColor", label: "Selected page" },
      { key: "sidebarAccentTextColor", label: "Selected page text" },
      { key: "sidebarHeaderTextColor", label: "Name at the top" },
    ],
  },
];

// "FFFFFF" without its # isn't a CSS color, and the app silently ignored it.
const withHash = (color: string) => {
  const c = color.trim();
  return /^([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(c) ? `#${c}` : color;
};

// The native picker only takes #rrggbb; any other CSS color still works in the text field.
const pickerValue = (color: string) => {
  const c = color.trim();
  if (/^#[0-9a-f]{6}$/i.test(c)) return c;
  if (/^#[0-9a-f]{3}$/i.test(c)) return `#${[...c.slice(1)].map((x) => x + x).join("")}`;
  return "#000000";
};

function ColorField({ id, label, value, onChange }: { id: string; label: string; value: string; onChange: (v: string) => void }) {
  return (
    <div className="flex items-end gap-2">
      <label
        className={`relative h-9 w-9 shrink-0 cursor-pointer rounded-md border focus-within:ring-2 focus-within:ring-ring ${value ? "" : "border-dashed"}`}
        style={value ? { backgroundColor: value } : undefined}
      >
        <input
          type="color"
          aria-label={`Pick ${label.toLowerCase()} color`}
          value={pickerValue(value)}
          onChange={(e) => onChange(e.target.value)}
          className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
        />
      </label>
      <div className="min-w-0 flex-1 space-y-1">
        <Label htmlFor={id} className="text-xs font-normal text-muted-foreground">
          {label}
        </Label>
        <Input
          id={id}
          value={value}
          placeholder="Default"
          onChange={(e) => onChange(e.target.value)}
          onBlur={() => withHash(value) !== value && onChange(withHash(value))}
          className="h-9 font-mono text-sm"
        />
      </div>
    </div>
  );
}

function AssetField({
  label,
  hint,
  url,
  busy,
  accept,
  cover,
  onFile,
  onRemove,
}: {
  label: string;
  hint: string;
  url: string;
  busy: boolean;
  accept: string;
  cover?: boolean;
  onFile: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRemove?: () => void;
}) {
  const fileInput = useRef<HTMLInputElement>(null);
  return (
    <div className="flex flex-wrap items-center gap-3">
      <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-md border bg-muted/40">
        {url ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img src={url} alt="" className={`h-full w-full ${cover ? "object-cover" : "object-contain"}`} />
        ) : (
          <span className="text-[11px] text-muted-foreground">None</span>
        )}
      </div>
      <div className="min-w-[10rem] flex-1">
        <p className="text-sm font-medium">{label}</p>
        <p className="text-xs text-muted-foreground">{hint}</p>
      </div>
      <input ref={fileInput} type="file" accept={accept} className="hidden" onChange={onFile} />
      <div className="flex gap-1">
        <Button type="button" variant="outline" size="sm" onClick={() => fileInput.current?.click()} disabled={busy}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="mr-1.5 h-4 w-4" />}
          {busy ? "" : url ? "Replace" : "Upload"}
        </Button>
        {url && onRemove && (
          <Button type="button" variant="ghost" size="sm" onClick={onRemove}>
            Remove
          </Button>
        )}
      </div>
    </div>
  );
}

/** What agents see: the sidebar and a primary button, from the unsaved form. */
function BrandPreview({ form }: { form: BrandingFormData }) {
  const sidebar = form.sidebarColor || "#1e293b";
  const text = form.sidebarTextColor || "#f8fafc";
  const size = Math.min(form.logoSize || 32, 40);
  // The same stylesheets the app loads once these fonts are saved.
  useEffect(() => {
    [form.textFont, form.headingFont].forEach((f) => isBrandFont(f) && brandFontCssUrls(f).forEach((href) => ensureFontCss(href)));
  }, [form.textFont, form.headingFont]);
  return (
    <Card className="h-fit">
      <CardHeader className="pb-3">
        <CardTitle className="!text-base">Preview</CardTitle>
        <CardDescription>How the sidebar looks with these settings, before you save.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div
          className="overflow-hidden rounded-lg border"
          style={{ backgroundColor: sidebar, color: text, fontFamily: form.textFont ? brandFontStack(form.textFont) : undefined }}
        >
          <div className="flex items-center gap-2 p-3">
            {form.logoUrl || form.faviconUrl ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img src={form.logoUrl || form.faviconUrl} alt="" className="rounded object-contain" style={{ width: size, height: size }} />
            ) : (
              <span
                className="flex items-center justify-center rounded text-xs font-bold text-white"
                style={{ width: size, height: size, backgroundColor: form.primaryColor || "#3b82f6" }}
              >
                {(form.brandName || "IC").charAt(0)}
              </span>
            )}
            <span
              className="truncate text-sm font-semibold"
              style={{
                color: form.sidebarHeaderTextColor || "#ffffff",
                fontFamily: form.headingFont ? brandFontStack(form.headingFont) : undefined,
              }}
            >
              {form.brandName || "InsuraCentral"}
            </span>
          </div>
          <div className="space-y-1 px-2 pb-3">
            {["Dashboard", "Leads", "Dialer"].map((item, i) => (
              <div
                key={item}
                className="rounded px-3 py-1.5 text-xs"
                style={
                  i === 0
                    ? { backgroundColor: form.sidebarAccentColor || "#334155", color: form.sidebarAccentTextColor || text }
                    : { opacity: 0.8 }
                }
              >
                {item}
              </div>
            ))}
          </div>
        </div>
        <span
          className="inline-flex h-8 items-center rounded-md px-3 text-xs font-medium text-white"
          style={{ backgroundColor: form.primaryColor || "#2563eb" }}
        >
          Add lead
        </span>
      </CardContent>
    </Card>
  );
}

export function BrandingSettings() {
  const { toast } = useToast();
  const { refreshBranding } = useBranding();
  const { user } = useUserContext();
  const { data: access } = useBrandingAccess();

  // An owner under an Enterprise agency brands only their own agency: name, logo and
  // colors. Fields they leave alone keep the upline's branding (the API stores overrides only).
  const isOwnerMode = user?.user?.accountType !== "enterprise";
  // The Enterprise agency itself: its owner decides whether the owners under it can brand.
  const canDelegate = access?.canDelegate === true;
  const contextAgencyId = user?.currentAgency?.agency?.id || user?.user?.agencyId || "";

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [form, setForm] = useState<BrandingFormData>(defaultFormData);
  const [agencyId, setAgencyId] = useState<string>("");
  const [uploadingAsset, setUploadingAsset] = useState<string | null>(null);

  // Load current branding data
  const loadBranding = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await axiosInstance.get("/api/branding/my-agency");
      const data = response?.data || response;
      const brandingData = data?.data || data;

      if (brandingData) {
        const loaded: BrandingFormData = {
          brandName: brandingData.brandName || "",
          brandTagline: brandingData.brandTagline || "",
          supportEmail: brandingData.supportEmail || "",
          supportPhone: brandingData.supportPhone || "",
          primaryColor: brandingData.primaryColor || "",
          secondaryColor: brandingData.secondaryColor || "",
          accentColor: brandingData.accentColor || "",
          sidebarColor: brandingData.sidebarColor || "",
          sidebarTextColor: brandingData.sidebarTextColor || "",
          sidebarAccentColor: brandingData.sidebarAccentColor || "",
          sidebarAccentTextColor: brandingData.sidebarAccentTextColor || "",
          sidebarHeaderTextColor: brandingData.sidebarHeaderTextColor || "",
          textFont: isBrandFont(brandingData.textFont) ? brandingData.textFont : null,
          headingFont: isBrandFont(brandingData.headingFont) ? brandingData.headingFont : null,
          logoUrl: brandingData.logoUrl || "",
          logoSize: brandingData.logoSize || 32,
          faviconUrl: brandingData.faviconUrl || "",
          loginBackgroundUrl: brandingData.loginBackgroundUrl || "",
          hidePoweredBy: brandingData.hidePoweredBy || false,
          brandingScope: brandingData.brandingScope || 'self_only',
        };
        // A color saved without its # gets it here, so the next save stores a working one.
        COLOR_GROUPS.forEach((g) => g.fields.forEach((f) => (loaded[f.key] = withHash(loaded[f.key]))));
        setForm(loaded);
        if (brandingData.agencyId) {
          setAgencyId(brandingData.agencyId);
        }
      }
    } catch (error: unknown) {
      console.error("Failed to load branding:", error);
      toast({
        title: "Couldn't load branding",
        description: "Refresh the page to try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  // Keyed on the agency, not the whole user object: a profile refresh mid-edit
  // reloaded the form and threw away unsaved changes.
  useEffect(() => {
    if (contextAgencyId) setAgencyId(contextAgencyId);
    loadBranding();
  }, [loadBranding, contextAgencyId]);

  const updateField = (field: keyof BrandingFormData, value: string | boolean | number | BrandFont | null) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleResetColors = () => {
    setForm((prev) => ({
      ...prev,
      primaryColor: "",
      secondaryColor: "",
      accentColor: "",
      sidebarColor: "",
      sidebarTextColor: "",
      sidebarAccentColor: "",
      sidebarAccentTextColor: "",
      sidebarHeaderTextColor: "",
    }));
  };

  // Handle file upload for branding assets
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>, assetType: string) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast({ title: "That file is over 5 MB", description: "Use a smaller image.", variant: "destructive" });
      return;
    }

    setUploadingAsset(assetType);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const aid = agencyId || user?.currentAgency?.agency?.id || user?.user?.agencyId;
      const response = await axiosInstance.post(
        `/api/branding/${aid}/upload/${assetType}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      const raw = response as any;
      const url = raw?.data?.data?.url || raw?.data?.url || raw?.url;

      if (!url) {
        console.error('Upload response missing URL:', raw);
        toast({ title: "Upload didn't finish", description: "No file address came back. Try again.", variant: "destructive" });
        return;
      }

      if (assetType === 'logo') {
        updateField('logoUrl', url);
      } else if (assetType === 'favicon') {
        updateField('faviconUrl', url);
      } else if (assetType === 'loginBackground') {
        updateField('loginBackgroundUrl', url);
      }

      toast({ title: assetType === 'logo' ? "Logo uploaded" : assetType === 'favicon' ? "Tab icon uploaded" : "Background uploaded" });

      // Refresh branding context so sidebar updates immediately
      if (refreshBranding) {
        await refreshBranding();
      }
    } catch (error: any) {
      toast({
        title: "Upload failed",
        description: error?.response?.data?.message || error?.message || "Try again.",
        variant: "destructive",
      });
    } finally {
      setUploadingAsset(null);
      e.target.value = '';
    }
  };

  const handleSave = async () => {
    if (!agencyId) {
      toast({
        title: "Couldn't find your agency",
        description: "Refresh the page and try again.",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsSaving(true);
      // Colors and images go as "" when cleared: an Enterprise save merges, so leaving
      // them out kept the old value and "Use default colors" / Remove did nothing.
      const payload: Partial<AgencyBranding> & { hidePoweredBy?: boolean } = {
        brandName: form.brandName || undefined,
        brandTagline: form.brandTagline || undefined,
        supportEmail: form.supportEmail || undefined,
        supportPhone: form.supportPhone || undefined,
        primaryColor: form.primaryColor,
        secondaryColor: form.secondaryColor,
        accentColor: form.accentColor,
        sidebarColor: form.sidebarColor,
        sidebarTextColor: form.sidebarTextColor,
        sidebarAccentColor: form.sidebarAccentColor,
        sidebarAccentTextColor: form.sidebarAccentTextColor,
        sidebarHeaderTextColor: form.sidebarHeaderTextColor,
        textFont: form.textFont,
        headingFont: form.headingFont,
        logoUrl: form.logoUrl || undefined,
        // Owner mode: always sent, or picking 32 under an upline's 60 would read as "inherit".
        logoSize: isOwnerMode || form.logoSize !== 32 ? form.logoSize : undefined,
        faviconUrl: form.faviconUrl,
        loginBackgroundUrl: form.loginBackgroundUrl,
        hidePoweredBy: form.hidePoweredBy,
        brandingScope: form.brandingScope || 'self_only',
      };

      await axiosInstance.put(`/api/branding/${agencyId}`, payload);

      toast({ title: "Branding saved" });

      if (refreshBranding) {
        await refreshBranding();
      }
    } catch (error: unknown) {
      console.error("Failed to save branding:", error);
      toast({
        title: "Couldn't save branding",
        description: error instanceof Error ? error.message : "Try again.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleResetToDefaults = async () => {
    if (!agencyId) return;

    try {
      setIsResetting(true);
      await axiosInstance.put(`/api/branding/${agencyId}`, {
        brandName: DEFAULT_BRANDING.brandName,
        brandTagline: DEFAULT_BRANDING.brandTagline,
        supportEmail: DEFAULT_BRANDING.supportEmail,
        supportPhone: "",
        primaryColor: "",
        secondaryColor: "",
        accentColor: "",
        sidebarColor: "",
        sidebarTextColor: "",
        sidebarAccentColor: "",
        sidebarAccentTextColor: "",
        sidebarHeaderTextColor: "",
        textFont: null,
        headingFont: null,
        logoUrl: "",
        faviconUrl: "",
        faviconPath: "",
        loginBackgroundUrl: "",
        loginBackgroundPath: "",
        logoSize: 32,
        hidePoweredBy: false,
        brandingScope: "self_only",
        _resetToDefaults: true,
      });

      setShowResetDialog(false);

      // Clear cached branding so BrandingProvider picks up defaults immediately
      try { localStorage.removeItem("insura_branding_cache"); } catch {}

      // Remove inline CSS variable overrides so default theme colors apply
      const root = document.documentElement;
      ["--primary", "--secondary", "--accent", "--sidebar", "--sidebar-background",
       "--sidebar-primary", "--sidebar-accent", "--sidebar-foreground",
       "--sidebar-primary-foreground", "--sidebar-accent-foreground", "--sidebar-header-foreground",
       "--default-font-family", "--brand-heading-font"].forEach(v => root.style.removeProperty(v));

      toast({
        title: isOwnerMode ? "Your branding is removed" : "Branding reset",
        description: isOwnerMode
          ? "Your agency is back on your upline's branding."
          : "Everything is back to InsuraCentral's defaults.",
      });

      if (refreshBranding) {
        await refreshBranding();
      }
      // Owner mode: the form shows what the agency sees now, the upline's branding.
      if (isOwnerMode) {
        await loadBranding();
      } else {
        setForm({ ...defaultFormData });
      }
    } catch (error: unknown) {
      console.error("Failed to reset branding:", error);
      toast({
        title: "Couldn't reset branding",
        description: "Try again.",
        variant: "destructive",
      });
    } finally {
      setIsResetting(false);
    }
  };

  if (isLoading || (isOwnerMode && !access)) {
    return (
      <div className="flex min-h-[300px] items-center justify-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading branding…
      </div>
    );
  }

  // Owner under an Enterprise agency that hasn't unlocked branding yet: the goal, not the editor.
  if (isOwnerMode && access && access.access !== "owner") {
    return <BrandingLockedCard access={access} />;
  }
  const ownerUnlockNote = isOwnerMode && access ? unlockNote(access) : null;
  const upline = access?.uplineName || "your upline";

  const brandForm = (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_18rem]">
      <Card>
        <CardContent className="divide-y p-0">
          <section className="space-y-5 p-6">
            <h3 className="!text-sm font-semibold">Name and logo</h3>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="brandName">Name</Label>
                <Input
                  id="brandName"
                  placeholder="Your agency name"
                  value={form.brandName}
                  onChange={(e) => updateField("brandName", e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="brandTagline">Tagline</Label>
                <Input
                  id="brandTagline"
                  placeholder="Shown in emails"
                  value={form.brandTagline}
                  onChange={(e) => updateField("brandTagline", e.target.value)}
                />
              </div>
            </div>
            <AssetField
              label="Logo"
              hint="Top of the sidebar. PNG, JPG, SVG or WebP, up to 5 MB."
              url={form.logoUrl}
              busy={uploadingAsset === "logo"}
              accept="image/png,image/jpeg,image/svg+xml,image/webp,image/gif,image/x-icon"
              onFile={(e) => handleFileUpload(e, "logo")}
            />
            <div className="flex items-center gap-3 sm:pl-[3.75rem]">
              <span id="logoSizeLabel" className="shrink-0 text-xs text-muted-foreground">
                Logo size
              </span>
              <Slider
                aria-labelledby="logoSizeLabel"
                min={24}
                max={80}
                step={4}
                value={[form.logoSize]}
                onValueChange={([v]) => updateField("logoSize", v)}
                className="max-w-xs flex-1"
              />
              <span className="w-10 text-xs tabular-nums text-muted-foreground">{form.logoSize}px</span>
            </div>
            <AssetField
              label="Browser tab icon"
              hint="Square works best. Falls back to the logo."
              url={form.faviconUrl}
              busy={uploadingAsset === "favicon"}
              accept="image/png,image/jpeg,image/svg+xml,image/webp,image/gif,image/x-icon"
              onFile={(e) => handleFileUpload(e, "favicon")}
              onRemove={() => updateField("faviconUrl", "")}
            />
          </section>

          <section className="space-y-5 p-6">
            <div className="flex items-center justify-between gap-2">
              <h3 className="!text-sm font-semibold">Colors</h3>
              <Button type="button" variant="ghost" size="sm" onClick={handleResetColors}>
                Use default colors
              </Button>
            </div>
            {COLOR_GROUPS.map((group) => (
              <div key={group.title} className="space-y-2">
                <p className="text-xs font-medium text-muted-foreground">{group.title}</p>
                <div className="grid gap-3 sm:grid-cols-2">
                  {group.fields.map((f) => (
                    <ColorField key={f.key} id={f.key} label={f.label} value={form[f.key]} onChange={(v) => updateField(f.key, v)} />
                  ))}
                </div>
              </div>
            ))}
          </section>

          <section className="space-y-5 p-6">
            <h3 className="!text-sm font-semibold">Fonts</h3>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="headingFont">Name and titles</Label>
                <FontPicker id="headingFont" kind="heading" value={form.headingFont} onChange={(f) => updateField("headingFont", f)} />
                <p className="text-xs text-muted-foreground">Your name at the top of the sidebar and page titles. Any style works.</p>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="textFont">All text</Label>
                <FontPicker id="textFont" kind="text" value={form.textFont} onChange={(f) => updateField("textFont", f)} />
                <p className="text-xs text-muted-foreground">Lead lists, forms, the dialer. Only easy-to-read fonts.</p>
              </div>
            </div>
          </section>

          {access?.hasAgenciesUnder && (
            <section className="flex items-start justify-between gap-4 p-6">
              <div>
                <Label htmlFor="brandingScope" className="!text-sm font-semibold">
                  Share with agencies under yours
                </Label>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  Agencies under yours that haven&apos;t set their own branding use yours.
                  {isOwnerMode && " On unless you turn it off."}
                </p>
              </div>
              <Switch
                id="brandingScope"
                checked={form.brandingScope === 'cascade'}
                onCheckedChange={(checked) => updateField("brandingScope", checked ? "cascade" : "self_only")}
              />
            </section>
          )}

          {!isOwnerMode && (
            <section className="space-y-5 p-6">
              <h3 className="!text-sm font-semibold">{access?.customDomain ? "Support and sign-in" : "Support"}</h3>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="supportEmail">Support email</Label>
                  <Input
                    id="supportEmail"
                    type="email"
                    placeholder="support@youragency.com"
                    value={form.supportEmail}
                    onChange={(e) => updateField("supportEmail", e.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="supportPhone">Support phone (optional)</Label>
                  <Input
                    id="supportPhone"
                    type="tel"
                    placeholder="(555) 123-4567"
                    value={form.supportPhone}
                    onChange={(e) => updateField("supportPhone", e.target.value)}
                  />
                </div>
              </div>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <Label htmlFor="hidePoweredBy" className="text-sm font-medium">
                    Hide &quot;Powered by InsuraCentral&quot;
                  </Label>
                  <p className="mt-0.5 text-xs text-muted-foreground">Leaves it off the emails the app sends for your agency.</p>
                </div>
                <Switch
                  id="hidePoweredBy"
                  checked={form.hidePoweredBy}
                  onCheckedChange={(checked) => updateField("hidePoweredBy", checked)}
                />
              </div>
              {/* The sign-in page is only branded on a custom domain, which we set up. */}
              {access?.customDomain && (
                <AssetField
                  label="Sign-in page background"
                  hint={`Shows on ${access.customDomain}. PNG, JPG or WebP, up to 5 MB.`}
                  url={form.loginBackgroundUrl}
                  cover
                  busy={uploadingAsset === "loginBackground"}
                  accept="image/png,image/jpeg,image/svg+xml,image/webp,image/gif"
                  onFile={(e) => handleFileUpload(e, "loginBackground")}
                  onRemove={() => updateField("loginBackgroundUrl", "")}
                />
              )}
            </section>
          )}
        </CardContent>
        <div className="flex flex-col-reverse gap-2 border-t px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
          <Button
            type="button"
            variant="ghost"
            onClick={() => setShowResetDialog(true)}
            className="text-red-600 hover:bg-red-50 hover:text-red-700"
          >
            <RotateCcw className="mr-2 h-4 w-4" />
            {isOwnerMode ? "Remove my branding" : "Reset to defaults"}
          </Button>
          <Button onClick={handleSave} disabled={isSaving} className="w-full sm:w-auto">
            {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isSaving ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </Card>
      <BrandPreview form={form} />
    </div>
  );

  return (
    <div className="space-y-6">
      <div>
        <h2 className="!text-xl font-semibold text-gray-900">Branding</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          {isOwnerMode
            ? `How the app looks for everyone in your agency. Anything you leave as it is stays ${upline}'s.`
            : "How the app looks for your agency and, if you share it, the agencies under yours."}
        </p>
        {ownerUnlockNote && <p className="mt-1 text-sm text-emerald-700">{ownerUnlockNote}</p>}
      </div>

      {canDelegate && agencyId ? (
        <Tabs defaultValue="brand" className="space-y-4">
          <TabsList>
            <TabsTrigger value="brand">Your brand</TabsTrigger>
            <TabsTrigger value="owners">Agency owners</TabsTrigger>
          </TabsList>
          <TabsContent value="brand">{brandForm}</TabsContent>
          <TabsContent value="owners">
            <OwnerBrandingProgramCard agencyId={agencyId} brandName={form.brandName || "your agency"} />
          </TabsContent>
        </Tabs>
      ) : (
        brandForm
      )}

      <Dialog open={showResetDialog} onOpenChange={setShowResetDialog}>
        <ResponsiveDialogContent>
          <DialogHeader>
            <DialogTitle>{isOwnerMode ? "Remove your branding?" : "Reset branding to defaults?"}</DialogTitle>
            <DialogDescription>
              {isOwnerMode
                ? `Your agency goes back to ${upline}'s name, logo and colors. Your agency logo stays on your agency profile.`
                : "Your name, colors and images go back to InsuraCentral's defaults. Your custom domain stays."}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowResetDialog(false)} disabled={isResetting}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleResetToDefaults} disabled={isResetting}>
              {isResetting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isOwnerMode ? "Remove branding" : "Reset to defaults"}
            </Button>
          </DialogFooter>
        </ResponsiveDialogContent>
      </Dialog>
    </div>
  );
}
